import { Typography } from "@mui/material";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

import { PolygonsType } from "@bxtech/bx-ui-lib/components/Mapbox/types";

import { useSnackbar } from "../useSnackbar";
import {
  getAllFieldsForEnterprise,
  getEnterpriseCentroids,
  getEnterpriseData,
  getEnterpriseOrganisationData,
  getEnterprisePolygons,
  getFarmDataByEnterprise,
  getFarmsFieldCiActionItemsByEnterprise,
  setEnterpriseState,
} from "@/lib/api";
import { FarmType, FieldType, OrganisationType } from "@/types/api";
import { Enterprise } from "@/types/dataTypes";
import { AtLeastOne } from "@/types/utilityTypes";
import { isValidId } from "@/utils/utils";

export type UseEnterpriseDataReturnType = {
  enterpriseQueryResult: UseQueryResult<Enterprise, unknown>;
  enterpriseOrganisationsQueryResult: UseQueryResult<
    OrganisationType[],
    unknown
  >;
  enterpriseFarmsQueryResult: UseQueryResult<FarmType[], unknown>;
  enterpriseFarmsCiActionItemsQueryResult: UseQueryResult<FarmType[], unknown>;
  enterpriseFieldsQueryResult: UseQueryResult<FieldType[], unknown>;
  enterpriseCentroidsQueryResult: UseQueryResult<
    GeoJSON.FeatureCollection,
    unknown
  >;
  enterprisePolygonsQueryResult: UseQueryResult<PolygonsType[], unknown>;
  setEnterpriseStateMutation: UseMutationResult<
    void,
    unknown,
    { stateName: string; value: boolean }
  >;
  subscribedAddons: Enterprise["subscribedAddons"];
  availableSubmodules: Enterprise["availableSubmodules"];
};

type UseEnterpriseDataOptions = AtLeastOne<{
  fetchEnterpriseData?: boolean;
  fetchEnterpriseOrganisationsData?: boolean;
  fetchEnterpriseFarmsData?: boolean;
  fetchEnterpriseFarmsCiActionItemsData?: boolean;
  fetchEnterpriseFieldsData?: boolean;
  fetchEnterpriseCentroidsData?: boolean;
  fetchEnterprisePolygonsData?: boolean;
  enrichPolygonsWith?: {
    yield?: boolean;
    cropInputs?: boolean;
    water?: boolean;
  };
  cropInputCategory?: string;
  mapFilters?: {
    years?: number[];
    cropIds?: number | number[];
    cropVarietyIds?: number[];
    farmIds?: number[];
  };
}>;

/**
 * Hook that handles the fetching logic for the enterprise data
 * @returns {UseEnterpriseDataReturnType}
 */

export const useEnterpriseData = (
  enterpriseId: string,
  options: UseEnterpriseDataOptions = {
    fetchEnterpriseData: false,
    fetchEnterpriseOrganisationsData: false,
    fetchEnterpriseFarmsData: false,
    fetchEnterpriseFarmsCiActionItemsData: false,
    fetchEnterpriseFieldsData: false,
    fetchEnterpriseCentroidsData: false,
    fetchEnterprisePolygonsData: false,
    enrichPolygonsWith: {
      yield: false,
      cropInputs: false,
      water: false,
    },
    mapFilters: undefined,
  }
): UseEnterpriseDataReturnType => {
  const isEnterpriseIdValid = isValidId(enterpriseId);
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const fetchCondition = (optionKey: keyof UseEnterpriseDataOptions) =>
    isEnterpriseIdValid && !!options[optionKey];

  const enterpriseQueryResult = useQuery(
    ["enterpriseData", enterpriseId],
    () => getEnterpriseData(enterpriseId),
    {
      enabled: fetchCondition("fetchEnterpriseData"),
      refetchOnWindowFocus: false,
      onError: () => {
        showSnackbar(
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle2">
              Oops, we couldn&apos;t load your data
            </Typography>
            <Typography variant="body2">
              Please refresh the page to try again
            </Typography>
          </div>,
          "error"
        );
      },
    }
  );

  const enrichKey = JSON.stringify(options.enrichPolygonsWith || {});
  const cropInputCategoryKey = options.cropInputCategory || "";
  const mapFiltersKey = JSON.stringify(options.mapFilters || {});

  return {
    enterpriseQueryResult,
    enterpriseOrganisationsQueryResult: useQuery(
      ["enterpriseOrganisations", enterpriseId],
      () => getEnterpriseOrganisationData(enterpriseId),
      {
        enabled: fetchCondition("fetchEnterpriseOrganisationsData"),
        refetchOnWindowFocus: false,
      }
    ),
    enterpriseFarmsQueryResult: useQuery(
      ["enterpriseFarms", enterpriseId],
      () => getFarmDataByEnterprise(enterpriseId),
      {
        enabled: fetchCondition("fetchEnterpriseFarmsData"),
        refetchOnWindowFocus: false,
      }
    ),
    enterpriseFarmsCiActionItemsQueryResult: useQuery(
      ["enterpriseFarmsCiActionItems", enterpriseId],
      () => getFarmsFieldCiActionItemsByEnterprise(enterpriseId),
      {
        enabled: fetchCondition("fetchEnterpriseFarmsCiActionItemsData"),
        refetchOnWindowFocus: false,
      }
    ),
    enterpriseFieldsQueryResult: useQuery(
      ["enterpriseFields", enterpriseId],
      () => getAllFieldsForEnterprise(enterpriseId),
      {
        enabled: fetchCondition("fetchEnterpriseFieldsData"),
        refetchOnWindowFocus: false,
      }
    ),
    enterpriseCentroidsQueryResult: useQuery(
      ["enterpriseCentroids", enterpriseId, mapFiltersKey],
      () => getEnterpriseCentroids(enterpriseId, options.mapFilters),
      {
        enabled:
          !!enterpriseId && fetchCondition("fetchEnterpriseCentroidsData"),
        refetchOnWindowFocus: false,
      }
    ),
    enterprisePolygonsQueryResult: useQuery(
      [
        "enterprisePolygons",
        enterpriseId,
        enrichKey,
        cropInputCategoryKey,
        mapFiltersKey,
      ],
      () =>
        getEnterprisePolygons(enterpriseId, {
          enrichYield: options.enrichPolygonsWith?.yield,
          enrichCropInputs: options.enrichPolygonsWith?.cropInputs,
          enrichWater: options.enrichPolygonsWith?.water,
          cropInputCategory: options.cropInputCategory,
          years: options.mapFilters?.years,
          cropIds: options.mapFilters?.cropIds,
          cropVarietyIds: options.mapFilters?.cropVarietyIds,
          farmIds: options.mapFilters?.farmIds,
        }),
      {
        enabled:
          !!enterpriseId && fetchCondition("fetchEnterprisePolygonsData"),
        refetchOnWindowFocus: false,
        structuralSharing: false,
        keepPreviousData: true,
      }
    ),
    setEnterpriseStateMutation: useMutation(
      ({ stateName, value }: { stateName: string; value: boolean }) => {
        if (!isEnterpriseIdValid) {
          throw new Error("Invalid enterprise ID");
        }
        return setEnterpriseState(enterpriseId, stateName, value);
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(["enterpriseData", enterpriseId]),
          ]);
          console.log("Succes: Onboarding complete, enterprise state updated");
        },
        onError: (error: Error) => {
          console.error("error", error);
          showSnackbar(`Failed to update state: ${error.message}`, "error");
        },
      }
    ),
    // New fields for easy access
    subscribedAddons: enterpriseQueryResult.data?.subscribedAddons || [],
    availableSubmodules: enterpriseQueryResult.data?.availableSubmodules || [],
  };
};
